/**
 * LeadRouterPage — Admin · Lead Auto-Router (Wave 1 + Wave 2)
 * 2026-06-30 · Stellar Builders sprint
 *
 * Layout: sidebar | graph (routing map SVG) | rule list (overlay + structural)
 * Side drawer: Lead Simulator — posts /simulate-v2, which runs the v2 resolver
 *   against RoutingAssignment (category → EXEC territory → manager escalation →
 *   dealer overlay). Every control is a DB-derived dropdown; READ-ONLY, it never
 *   assigns a lead. Pure logic lives in ./leadSimulatorV2Logic.
 *   The legacy v1 POST /simulate client is untouched and still used by the
 *   routing-graph highlight bridge (toLegacyHighlight).
 *
 * API:
 *   GET    /admin/lead-router/coverage           — load all rules, staff, settings
 *   POST   /admin/lead-router/simulate-v2        — trace a lead through the v2 resolver
 *   POST   /admin/lead-router/simulate-v2/batch  — same, over a grid of leads
 *   GET    /admin/lead-router/geo/{states,districts,pincodes,pincode/:p}
 *   POST   /admin/lead-router/simulate           — LEGACY v1 (kept, still consumed)
 *   PUT    /admin/lead-router/settings           — toggle autoRouterEnabled
 *   GET/POST/PATCH/DELETE /admin/lead-router/overlay-rules (+ /reorder)
 *   PATCH/DELETE  /admin/lead-router/category-rules/:id
 *   PATCH/DELETE  /admin/lead-router/territory-rules/:id
 *   PATCH/DELETE  /admin/lead-router/dealers/:id
 *
 * Design constraints:
 *   - NO `primary` Tailwind. All colour via var(--…) CSS tokens.
 *   - Deps NOT locally installed — build happens on box; mirror import shapes.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Play,
  Plus,
  RefreshCcw,
  Settings2,
  Users,
  X,
  Zap,
} from 'lucide-react';
import {
  leadRouterApi,
  leadRouterV2Api,
  type CategoryResolutionRule,
  type CoverageData,
  type GeoPincode,
  type OverlayCondition,
  type OverlayRule,
  type OverlayTarget,
  type RouterDealer,
  type RouterStaff,
  type RoutingProductGroupV2,
  type SimulateInput,
  type SimulateResult,
  type SimulateV2BatchResult,
  type SimulateV2Result,
} from '@/api/leadRouter';
import {
  APP_TYPE_DISABLED_NOTE,
  BATCH_LIMIT,
  CITY_NOTE,
  HUMAN_TOUCHED_NOTE,
  PINCODE_CONFIDENT_NOTE,
  applyCategoryChange,
  applyPincodeCleared,
  applyPincodeResolved,
  applyStateChange,
  buildSimulateV2Payload,
  checkBatchSize,
  deriveApplicationTypeOptions,
  deriveCategoryOptions,
  describeAssertion,
  destinationLabel,
  emptySimV2Form,
  expandBatchLeads,
  isCategoryApplicationTyped,
  isValidPincode,
  toLegacyHighlight,
  winnerLabel,
  type BatchVaryBy,
  type SimV2FormState,
} from './leadSimulatorV2Logic';

// ─── Local helpers ────────────────────────────────────────────────────────────

function staffName(s: RouterStaff): string {
  return s.name || s.email;
}

function initials(name: string): string {
  return name
    .split(' ')
    .map((x) => x[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

const AVATAR_COLORS = [
  '#E07A16',
  '#1F4B3A',
  '#2A6E8A',
  '#8A2A3A',
  '#B55A00',
  '#5B6E3A',
];

function avatarColor(name: string): string {
  return AVATAR_COLORS[(name.charCodeAt(0) || 0) % AVATAR_COLORS.length];
}

// ─── Small primitives ─────────────────────────────────────────────────────────

function AvatarChip({ name, size = 26 }: { name: string; size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: avatarColor(name),
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.38,
        fontWeight: 650,
        flexShrink: 0,
      }}
    >
      {initials(name)}
    </div>
  );
}

type PillTone = 'green' | 'red' | 'amber' | 'neutral' | 'ink';
const PILL_TONES: Record<PillTone, { bg: string; fg: string; bd: string }> = {
  green:   { bg: 'var(--forest-50)',   fg: 'var(--forest-600)',  bd: 'var(--forest-100)' },
  red:     { bg: 'var(--danger-bg)',   fg: 'var(--danger)',       bd: '#EFCFCA' },
  amber:   { bg: 'var(--saffron-50)',  fg: 'var(--saffron-600)', bd: 'var(--saffron-100)' },
  neutral: { bg: 'var(--ivory-2)',     fg: 'var(--ink-2)',        bd: 'var(--line)' },
  ink:     { bg: 'var(--ink-0)',       fg: 'var(--ivory-0)',      bd: 'var(--ink-0)' },
};

function StatusPill({ tone = 'neutral', children }: { tone?: PillTone; children: React.ReactNode }) {
  const t = PILL_TONES[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        height: 20,
        padding: '0 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.bd}`,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

export function Toggle({
  on,
  onChange,
  tone = 'forest',
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  tone?: 'forest' | 'saffron';
}) {
  const col = tone === 'forest' ? 'var(--forest)' : 'var(--saffron)';
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      aria-pressed={on}
      style={{
        width: 38,
        height: 22,
        borderRadius: 22,
        border: 'none',
        padding: 2,
        cursor: 'pointer',
        flexShrink: 0,
        background: on ? col : 'var(--line-strong)',
        transition: 'background .18s',
        position: 'relative',
      }}
    >
      <span
        style={{
          display: 'block',
          width: 18,
          height: 18,
          borderRadius: 18,
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,.3)',
          transform: on ? 'translateX(16px)' : 'translateX(0)',
          transition: 'transform .18s',
        }}
      />
    </button>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ id: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div
      style={{
        display: 'inline-flex',
        gap: 3,
        background: 'var(--ivory-2)',
        border: '1px solid var(--line)',
        borderRadius: 9,
        padding: 3,
      }}
    >
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            style={{
              padding: '4px 10px',
              fontSize: 12,
              fontWeight: on ? 600 : 500,
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              background: on ? 'var(--ivory-0)' : 'transparent',
              color: on ? 'var(--ink-0)' : 'var(--ink-2)',
              boxShadow: on ? 'var(--sh-1)' : 'none',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Field/value select helpers ───────────────────────────────────────────────

const OVERLAY_FIELDS = [
  { value: 'capturedCategory',  label: 'Product category',   options: null as string[] | null },
  { value: 'applicationType',   label: 'Application type',   options: null as string[] | null },
  { value: 'locationState',     label: 'State',              options: null as string[] | null },
  { value: 'location',          label: 'City / Location',    options: null as string[] | null },
  { value: 'interestLevel',     label: 'Interest level',     options: ['high', 'medium', 'low'] },
  { value: 'isB2bPriority',     label: 'B2B priority',       options: ['true', 'false'] },
];

const OVERLAY_OPS = [
  { value: 'is',       label: 'is' },
  { value: 'is_not',  label: 'is not' },
  { value: 'in',      label: 'is any of' },
  { value: 'contains',label: 'contains' },
];

// ─── ConditionRow ─────────────────────────────────────────────────────────────

interface CondItem { field: string; op: string; value: string }

function ConditionRow({
  cond,
  onChange,
  onRemove,
}: {
  cond: CondItem;
  onChange: (c: CondItem) => void;
  onRemove: () => void;
}) {
  const fieldDef = OVERLAY_FIELDS.find((f) => f.value === cond.field) ?? OVERLAY_FIELDS[0];
  const sStyle: React.CSSProperties = {
    height: 30,
    padding: '0 8px',
    fontSize: 12,
    color: 'var(--ink-0)',
    background: 'var(--ivory-0)',
    border: '1px solid var(--line)',
    borderRadius: 6,
    outline: 'none',
    fontFamily: 'inherit',
    cursor: 'pointer',
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px 6px',
        background: 'var(--ivory-0)',
        border: '1px solid var(--line)',
        borderRadius: 7,
      }}
    >
      <GripVertical size={13} style={{ color: 'var(--ink-4)', flexShrink: 0 }} />

      <select
        value={cond.field}
        onChange={(e) => onChange({ ...cond, field: e.target.value })}
        style={{ ...sStyle, width: 148 }}
      >
        {OVERLAY_FIELDS.map((f) => (
          <option key={f.value} value={f.value}>
            {f.label}
          </option>
        ))}
      </select>

      <select
        value={cond.op}
        onChange={(e) => onChange({ ...cond, op: e.target.value })}
        style={{ ...sStyle, width: 90 }}
      >
        {OVERLAY_OPS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      {fieldDef.options ? (
        <select
          value={cond.value}
          onChange={(e) => onChange({ ...cond, value: e.target.value })}
          style={{ ...sStyle, flex: 1, minWidth: 80 }}
        >
          {fieldDef.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <input
          value={cond.value}
          onChange={(e) => onChange({ ...cond, value: e.target.value })}
          placeholder="value"
          style={{ ...sStyle, flex: 1, minWidth: 80 }}
        />
      )}

      <button
        type="button"
        onClick={onRemove}
        style={{
          width: 24,
          height: 24,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: 'var(--ink-3)',
          borderRadius: 5,
          flexShrink: 0,
        }}
        aria-label="remove condition"
      >
        <X size={12} />
      </button>
    </div>
  );
}

// ─── TargetPicker ─────────────────────────────────────────────────────────────

function TargetPicker({
  target,
  onChange,
  staff,
  dealers,
}: {
  target: OverlayTarget;
  onChange: (t: OverlayTarget) => void;
  staff: RouterStaff[];
  dealers: RouterDealer[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const label = useMemo(() => {
    if (target.action === 'assign_user' && target.ownerUserId) {
      const u = staff.find((s) => s.id === target.ownerUserId);
      return u ? staffName(u) : target.ownerUserId;
    }
    if (target.action === 'assign_dealer' && target.dealerId) {
      const d = dealers.find((dl) => dl.id === target.dealerId);
      return d ? d.name : target.dealerId;
    }
    return target.action.replace(/_/g, ' ');
  }, [target, staff, dealers]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          height: 30,
          padding: '0 10px',
          fontSize: 12,
          fontWeight: 500,
          border: '1px solid var(--line)',
          borderRadius: 7,
          background: 'var(--ivory-0)',
          cursor: 'pointer',
          color: 'var(--ink-1)',
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {target.action === 'assign_user' && target.ownerUserId && (
          <AvatarChip name={label} size={18} />
        )}
        {target.action === 'assign_dealer' && <Users size={13} />}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        <ChevronDown size={12} style={{ opacity: 0.5, flexShrink: 0 }} />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            width: 240,
            maxHeight: 300,
            overflowY: 'auto',
            background: 'var(--ivory-0)',
            border: '1px solid var(--line)',
            borderRadius: 10,
            boxShadow: 'var(--sh-4)',
            zIndex: 70,
          }}
        >
          <div
            style={{
              padding: '5px 9px',
              fontSize: 10,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.06,
              fontWeight: 600,
            }}
          >
            Staff
          </div>
          {staff.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                onChange({ action: 'assign_user', ownerUserId: s.id, dealerId: null });
                setOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                width: '100%',
                padding: '7px 10px',
                border: 'none',
                background:
                  target.action === 'assign_user' && target.ownerUserId === s.id
                    ? 'var(--ivory-2)'
                    : 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <AvatarChip name={staffName(s)} size={20} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ink-0)' }}>
                  {staffName(s)}
                </div>
                <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>{s.roleName ?? s.email}</div>
              </div>
              {target.action === 'assign_user' && target.ownerUserId === s.id && (
                <Check size={13} style={{ color: 'var(--forest-600)', flexShrink: 0 }} />
              )}
            </button>
          ))}

          {dealers.length > 0 && (
            <>
              <div style={{ height: 1, background: 'var(--line)', margin: '4px 0' }} />
              <div
                style={{
                  padding: '5px 9px',
                  fontSize: 10,
                  color: 'var(--ink-3)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.06,
                  fontWeight: 600,
                }}
              >
                Dealers
              </div>
              {dealers.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => {
                    onChange({ action: 'assign_dealer', dealerId: d.id, ownerUserId: null });
                    setOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 9,
                    width: '100%',
                    padding: '7px 10px',
                    border: 'none',
                    background:
                      target.action === 'assign_dealer' && target.dealerId === d.id
                        ? 'var(--ivory-2)'
                        : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <Users size={16} style={{ color: 'var(--ink-3)' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ink-0)' }}>
                      {d.name}
                    </div>
                  </div>
                  {target.action === 'assign_dealer' && target.dealerId === d.id && (
                    <Check size={13} style={{ color: 'var(--forest-600)', flexShrink: 0 }} />
                  )}
                </button>
              ))}
            </>
          )}

          <div style={{ height: 1, background: 'var(--line)', margin: '4px 0' }} />
          <button
            type="button"
            onClick={() => {
              onChange({ action: 'manual_deepika' });
              setOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              width: '100%',
              padding: '7px 10px',
              border: 'none',
              background:
                target.action === 'manual_deepika' ? 'var(--ivory-2)' : 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <Zap size={14} style={{ color: 'var(--saffron-600)' }} />
            <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ink-1)' }}>
              Manual triage (fallback)
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── RouterGraph ──────────────────────────────────────────────────────────────
// 3-column SVG edge graph: Lead → Overlay rules → Staff

interface GraphEdge {
  ax: number; ay: number;
  bx: number; by: number;
  active: boolean;
  dim: boolean;
  fallback?: boolean;
}

function RouterGraph({
  overlayRules,
  staff,
  dealers,
  activeLead,
  simResult,
}: {
  overlayRules: OverlayRule[];
  staff: RouterStaff[];
  dealers: RouterDealer[];
  activeLead: SimulateInput | null;
  simResult: SimulateResult | null;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<Record<string, HTMLElement>>({});
  const [edges, setEdges] = useState<GraphEdge[]>([]);

  const setRef = (k: string) => (el: HTMLElement | null) => {
    if (el) nodeRefs.current[k] = el;
  };

  const recompute = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const wr = wrap.getBoundingClientRect();
    const center = (k: string, side: 'l' | 'r') => {
      const el = nodeRefs.current[k];
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return {
        x: (side === 'r' ? r.right : r.left) - wr.left,
        y: r.top - wr.top + r.height / 2,
      };
    };

    const es: GraphEdge[] = [];
    const matchedRuleId = simResult?.overlayRuleId;

    overlayRules
      .filter((r) => r.enabled)
      .forEach((r) => {
        const a = center('lead', 'r');
        const b = center('rule-' + r.id, 'l');
        if (a && b)
          es.push({
            ax: a.x, ay: a.y, bx: b.x, by: b.y,
            active: !!activeLead && matchedRuleId === r.id,
            dim: !!activeLead && matchedRuleId !== r.id,
          });
      });

    overlayRules
      .filter((r) => r.enabled)
      .forEach((r) => {
        let targetKey: string | null = null;
        if (r.target.action === 'assign_user' && r.target.ownerUserId) {
          targetKey = 'staff-' + r.target.ownerUserId;
        } else if (r.target.action === 'assign_dealer' && r.target.dealerId) {
          targetKey = 'dealer-' + r.target.dealerId;
        } else {
          targetKey = 'action-' + r.target.action;
        }
        const a = center('rule-' + r.id, 'r');
        const b = targetKey ? center(targetKey, 'l') : null;
        if (a && b)
          es.push({
            ax: a.x, ay: a.y, bx: b.x, by: b.y,
            active: !!activeLead && matchedRuleId === r.id,
            dim: !!activeLead && matchedRuleId !== r.id,
          });
      });

    // fallback edge if decided by 4-stage (no overlay match)
    if (activeLead && simResult && simResult.decidedBy === '4-stage') {
      const a = center('lead', 'r');
      const b = center('stage-engine', 'l');
      if (a && b)
        es.push({ ax: a.x, ay: a.y, bx: b.x, by: b.y, active: true, dim: false, fallback: true });
    }

    setEdges(es);
  }, [overlayRules, activeLead, simResult]);

  useEffect(() => {
    recompute();
    const t = setTimeout(recompute, 80);
    window.addEventListener('resize', recompute);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', recompute);
    };
  }, [recompute]);

  const assignedStaffIds = new Set(
    overlayRules
      .filter((r) => r.target.action === 'assign_user' && r.target.ownerUserId)
      .map((r) => r.target.ownerUserId as string),
  );
  const assignedDealerIds = new Set(
    overlayRules
      .filter((r) => r.target.action === 'assign_dealer' && r.target.dealerId)
      .map((r) => r.target.dealerId as string),
  );
  const shownStaff = staff.filter((s) => assignedStaffIds.has(s.id));
  const shownDealers = dealers.filter((d) => assignedDealerIds.has(d.id));

  const col = (title: string, children: React.ReactNode) => (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.1,
          textAlign: 'center',
          marginBottom: 2,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );

  const nodeCard = (
    key: string,
    isTarget: boolean,
    children: React.ReactNode,
  ) => (
    <div
      ref={setRef(key) as any}
      style={{
        background: isTarget ? 'var(--forest-50)' : 'var(--ivory-0)',
        border: `1.5px solid ${isTarget ? 'var(--forest)' : 'var(--line)'}`,
        borderRadius: 10,
        padding: '8px 10px',
        boxShadow: 'var(--sh-1)',
        opacity: activeLead && !isTarget ? 0.5 : 1,
        transition: 'opacity .2s, border-color .2s',
      }}
    >
      {children}
    </div>
  );

  return (
    <div ref={wrapRef} style={{ position: 'relative', padding: '18px 22px', minHeight: 320 }}>
      <svg
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          overflow: 'visible',
        }}
      >
        {edges.map((e, i) => {
          const mx = (e.ax + e.bx) / 2;
          const d = `M ${e.ax} ${e.ay} C ${mx} ${e.ay}, ${mx} ${e.by}, ${e.bx} ${e.by}`;
          return (
            <path
              key={i}
              d={d}
              fill="none"
              stroke={
                e.active
                  ? e.fallback
                    ? 'var(--saffron)'
                    : 'var(--forest)'
                  : 'var(--line-strong)'
              }
              strokeWidth={e.active ? 2.4 : 1.3}
              strokeDasharray={e.fallback ? '5 4' : undefined}
              opacity={e.dim ? 0.3 : 1}
              style={{ transition: 'stroke .2s, opacity .2s' }}
            />
          );
        })}
      </svg>

      <div style={{ display: 'flex', gap: 52, alignItems: 'flex-start', position: 'relative' }}>
        {/* Column 1: Incoming lead */}
        {col(
          'Incoming lead',
          <div
            ref={setRef('lead') as any}
            style={{
              background: activeLead ? 'var(--ivory-1)' : 'var(--ivory-0)',
              border: `1.5px solid ${activeLead ? 'var(--forest)' : 'var(--line)'}`,
              borderRadius: 11,
              padding: 12,
              boxShadow: 'var(--sh-1)',
              minWidth: 160,
            }}
          >
            {activeLead ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {[
                  ['Category', activeLead.capturedCategory],
                  ['App type', activeLead.applicationType],
                  ['State', activeLead.locationState],
                  ['City', activeLead.location],
                  ['Interest', activeLead.interestLevel],
                  ['B2B', activeLead.isB2bPriority != null ? String(activeLead.isB2bPriority) : null],
                ]
                  .filter(([, v]) => v != null)
                  .map(([k, v]) => (
                    <div key={k as string} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                      <span style={{ color: 'var(--ink-3)' }}>{k}</span>
                      <span style={{ color: 'var(--ink-1)', fontWeight: 500 }}>{v}</span>
                    </div>
                  ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--ink-3)', fontSize: 12, padding: '14px 4px' }}>
                <Play size={18} style={{ color: 'var(--ink-4)', marginBottom: 6, display: 'block', margin: '0 auto 6px' }} />
                Run the simulator to trace a lead.
              </div>
            )}
          </div>,
        )}

        {/* Column 2: Overlay rules */}
        {col(
          `Overlay rules · ${overlayRules.length}`,
          overlayRules.length === 0 ? (
            <div
              style={{
                fontSize: 11,
                color: 'var(--ink-3)',
                textAlign: 'center',
                padding: '20px 8px',
                border: '1px dashed var(--line)',
                borderRadius: 8,
              }}
            >
              No overlay rules yet. Add one in the panel.
            </div>
          ) : (
            overlayRules.map((r) => {
              const isMatch = activeLead && simResult?.overlayRuleId === r.id;
              return nodeCard(
                'rule-' + r.id,
                !!isMatch,
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'var(--ink-3)',
                      width: 14,
                      fontFamily: 'var(--ff-mono)',
                    }}
                  >
                    {r.priority}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--ink-0)',
                      flex: 1,
                      lineHeight: 1.2,
                    }}
                  >
                    {r.name}
                  </span>
                  {!r.enabled && (
                    <span style={{ fontSize: 9, color: 'var(--ink-4)' }}>OFF</span>
                  )}
                  {isMatch && <Check size={14} style={{ color: 'var(--forest-600)' }} />}
                </div>,
              );
            })
          ),
        )}

        {/* Column 3: Assignees + 4-stage fallback */}
        {col(
          'Assignee',
          <>
            {shownStaff.map((s) => {
              const isTarget =
                activeLead &&
                simResult?.decidedBy === 'overlay' &&
                simResult.assignee?.userId === s.id;
              return nodeCard(
                'staff-' + s.id,
                !!isTarget,
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AvatarChip name={staffName(s)} size={24} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-0)' }}>
                      {staffName(s)}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>{s.roleName}</div>
                  </div>
                  {isTarget && <StatusPill tone="green">assigned</StatusPill>}
                </div>,
              );
            })}
            {shownDealers.map((d) => {
              const isTarget =
                activeLead &&
                simResult?.decidedBy === 'overlay' &&
                simResult.dealerId === d.id;
              return nodeCard(
                'dealer-' + d.id,
                !!isTarget,
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Users size={16} style={{ color: 'var(--ink-3)' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-0)' }}>
                      {d.name}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Dealer</div>
                  </div>
                </div>,
              );
            })}
            {/* 4-stage engine node always visible */}
            {nodeCard(
              'stage-engine',
              !!(activeLead && simResult?.decidedBy === '4-stage'),
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Settings2 size={16} style={{ color: 'var(--ink-3)' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-0)' }}>
                    4-stage router
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                    Category → Territory → Dealer
                  </div>
                  {simResult?.decidedBy === '4-stage' && simResult.assignee && (
                    <div style={{ fontSize: 11, color: 'var(--forest-600)', marginTop: 3, fontWeight: 600 }}>
                      → {simResult.assignee.name ?? simResult.assignee.userId}
                    </div>
                  )}
                </div>
                {activeLead && simResult?.decidedBy === '4-stage' && (
                  <StatusPill tone="amber">via engine</StatusPill>
                )}
              </div>,
            )}
          </>,
        )}
      </div>
    </div>
  );
}

// ─── OverlayRuleCard ──────────────────────────────────────────────────────────

function OverlayRuleCard({
  rule,
  expanded,
  onToggleExpand,
  onChange,
  onDragStart,
  onDelete,
  simStatus,
  staff,
  dealers,
  saving,
}: {
  rule: OverlayRule;
  expanded: boolean;
  onToggleExpand: () => void;
  onChange: (r: OverlayRule) => void;
  onDragStart: (e: React.PointerEvent) => void;
  onDelete: () => void;
  simStatus: 'matched' | 'skipped' | null;
  staff: RouterStaff[];
  dealers: RouterDealer[];
  saving: boolean;
}) {
  const conds: CondItem[] = (rule.conditions?.items ?? []) as CondItem[];

  const setCondItem = (i: number, c: CondItem) => {
    const items = conds.map((x, j) => (j === i ? c : x));
    onChange({ ...rule, conditions: { ...rule.conditions, items } });
  };

  const removeCondItem = (i: number) => {
    onChange({
      ...rule,
      conditions: { ...rule.conditions, items: conds.filter((_, j) => j !== i) },
    });
  };

  const addCondItem = () => {
    onChange({
      ...rule,
      conditions: {
        ...rule.conditions,
        items: [...conds, { field: 'capturedCategory', op: 'is', value: '' }],
      },
    });
  };

  const borderColor = simStatus === 'matched' ? 'var(--forest)' : 'var(--line)';

  return (
    <div
      style={{
        background: 'var(--ivory-0)',
        border: `1.5px solid ${borderColor}`,
        borderRadius: 11,
        boxShadow:
          simStatus === 'matched'
            ? '0 0 0 3px rgba(47,122,91,.13), var(--sh-1)'
            : 'var(--sh-1)',
        opacity: rule.enabled ? 1 : 0.62,
        overflow: 'hidden',
        transition: 'box-shadow .2s, border-color .2s',
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          padding: '10px 12px',
        }}
      >
        <span
          onPointerDown={onDragStart}
          style={{ color: 'var(--ink-4)', cursor: 'grab', display: 'flex', flexShrink: 0 }}
          title="Drag to set priority"
        >
          <GripVertical size={15} />
        </span>

        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            background: 'var(--ivory-2)',
            border: '1px solid var(--line)',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--ink-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontFamily: 'var(--ff-mono)',
          }}
        >
          {rule.priority}
        </span>

        <div
          style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
          onClick={onToggleExpand}
        >
          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink-0)' }}>
            {rule.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              {conds.length} condition{conds.length !== 1 ? 's' : ''} ·{' '}
              <b style={{ color: 'var(--ink-2)' }}>{rule.conditions?.op ?? 'AND'}</b>
            </span>
            {simStatus === 'matched' && (
              <StatusPill tone="green">
                <Check size={10} style={{ marginRight: 2 }} /> matched
              </StatusPill>
            )}
            {simStatus === 'skipped' && <StatusPill tone="neutral">skipped (off)</StatusPill>}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>→</span>
          <TargetPicker
            target={rule.target}
            onChange={(t) => onChange({ ...rule, target: t })}
            staff={staff}
            dealers={dealers}
          />
          <Toggle on={rule.enabled} onChange={(v) => onChange({ ...rule, enabled: v })} />
          <button
            type="button"
            onClick={onDelete}
            disabled={saving}
            title="Delete rule"
            style={{
              width: 24,
              height: 24,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              background: 'transparent',
              cursor: saving ? 'not-allowed' : 'pointer',
              color: 'var(--danger)',
              borderRadius: 5,
              opacity: saving ? 0.5 : 0.7,
            }}
          >
            <X size={13} />
          </button>
          <button
            type="button"
            onClick={onToggleExpand}
            style={{
              width: 24,
              height: 24,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--ink-3)',
              borderRadius: 5,
            }}
          >
            {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          </button>
        </div>
      </div>

      {/* Expanded: condition builder */}
      {expanded && (
        <div
          style={{
            padding: 12,
            borderTop: '1px solid var(--line)',
            background: 'var(--ivory-1)',
          }}
        >
          {/* Name edit */}
          <div style={{ marginBottom: 10 }}>
            <label
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--ink-2)',
                textTransform: 'uppercase',
                letterSpacing: 0.06,
                display: 'block',
                marginBottom: 5,
              }}
            >
              Rule name
            </label>
            <input
              value={rule.name}
              onChange={(e) => onChange({ ...rule, name: e.target.value })}
              style={{
                width: '100%',
                height: 30,
                padding: '0 9px',
                fontSize: 13,
                color: 'var(--ink-0)',
                background: 'var(--ivory-0)',
                border: '1px solid var(--line)',
                borderRadius: 7,
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* AND / OR toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--ink-2)',
                textTransform: 'uppercase',
                letterSpacing: 0.06,
              }}
            >
              Match
            </span>
            <Segmented
              value={(rule.conditions?.op ?? 'AND') as 'AND' | 'OR'}
              onChange={(op) =>
                onChange({ ...rule, conditions: { ...rule.conditions, op } })
              }
              options={[
                { id: 'AND', label: 'All (AND)' },
                { id: 'OR',  label: 'Any (OR)' },
              ]}
            />
          </div>

          {/* Conditions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {conds.map((c, i) => (
              <ConditionRow
                key={i}
                cond={c}
                onChange={(cc) => setCondItem(i, cc)}
                onRemove={() => removeCondItem(i)}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={addCondItem}
            style={{
              marginTop: 8,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              height: 28,
              padding: '0 10px',
              fontSize: 12,
              fontWeight: 500,
              border: '1px solid var(--line)',
              borderRadius: 7,
              background: 'var(--ivory-0)',
              cursor: 'pointer',
              color: 'var(--ink-1)',
            }}
          >
            <Plus size={12} /> Add condition
          </button>
        </div>
      )}
    </div>
  );
}

// ─── SimulatorDrawer ──────────────────────────────────────────────────────────

const SIM_LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--ink-2)',
  textTransform: 'uppercase',
  letterSpacing: 0.06,
  marginBottom: 5,
};

const SIM_CTRL: React.CSSProperties = {
  width: '100%',
  height: 32,
  padding: '0 9px',
  fontSize: 13,
  color: 'var(--ink-0)',
  background: 'var(--ivory-0)',
  border: '1px solid var(--line)',
  borderRadius: 7,
  outline: 'none',
  fontFamily: 'inherit',
};

function SimNote({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 10.5, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.45 }}>
      {children}
    </div>
  );
}

function SimField({
  label,
  children,
  note,
}: {
  label: string;
  children: React.ReactNode;
  note?: React.ReactNode;
}) {
  return (
    <div>
      <label style={SIM_LABEL}>{label}</label>
      {children}
      {note ? <SimNote>{note}</SimNote> : null}
    </div>
  );
}

/** Two-state switch row used for pincodeConfident / humanTouched. */
function SimSwitch({
  label,
  value,
  onChange,
  note,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  note?: React.ReactNode;
}) {
  return (
    <div>
      <label style={SIM_LABEL}>{label}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        {[
          { l: 'Off', v: false },
          { l: 'On', v: true },
        ].map(({ l, v }) => (
          <button
            key={l}
            type="button"
            onClick={() => onChange(v)}
            style={{
              flex: 1,
              height: 30,
              fontSize: 12,
              fontWeight: value === v ? 600 : 400,
              border: '1px solid var(--line)',
              borderRadius: 7,
              background: value === v ? 'var(--ivory-2)' : 'var(--ivory-0)',
              cursor: 'pointer',
              color: value === v ? 'var(--ink-0)' : 'var(--ink-2)',
            }}
          >
            {l}
          </button>
        ))}
      </div>
      {note ? <SimNote>{note}</SimNote> : null}
    </div>
  );
}

/**
 * Lead Simulator — posts to the **v2** resolver (`/lead-router/simulate-v2`),
 * which reads RoutingAssignment. Every control that can be a real dropdown is
 * one; the option lists are derived from the tenant's own DB rows
 * (CategoryResolutionRule + RoutingProductGroup + PincodeArea), never hardcoded.
 * READ-ONLY: nothing here writes a routing decision or mutates a lead.
 *
 * All decision logic lives in ./leadSimulatorV2Logic (pure + unit-tested);
 * this component is wiring.
 */
export function SimulatorDrawer({
  open,
  onClose,
  staff,
  dealers,
  categoryRules,
  onResult,
}: {
  open: boolean;
  onClose: () => void;
  staff: RouterStaff[];
  dealers: RouterDealer[];
  /** Optional — when omitted the drawer loads coverage itself on first open. */
  categoryRules?: CategoryResolutionRule[];
  onResult?: (lead: SimulateInput, result: SimulateResult) => void;
}) {
  const [mode, setMode] = useState<'single' | 'batch'>('single');
  const [form, setForm] = useState<SimV2FormState>(emptySimV2Form);
  const [result, setResult] = useState<SimulateV2Result | null>(null);
  const [batch, setBatch] = useState<SimulateV2BatchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Option sources (all DB-driven) ─────────────────────────────────────────
  const [rules, setRules] = useState<CategoryResolutionRule[]>(categoryRules ?? []);
  const [groups, setGroups] = useState<RoutingProductGroupV2[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [districts, setDistricts] = useState<string[]>([]);
  const [pinHits, setPinHits] = useState<GeoPincode[]>([]);
  const [geoDown, setGeoDown] = useState(false);

  // ── Batch axis ─────────────────────────────────────────────────────────────
  const [varyBy, setVaryBy] = useState<BatchVaryBy>('state');
  const [picked, setPicked] = useState<string[]>([]);

  useEffect(() => {
    if (categoryRules) setRules(categoryRules);
  }, [categoryRules]);

  useEffect(() => {
    if (!open) return;
    let dead = false;
    void (async () => {
      if (!categoryRules) {
        try {
          const cov = await leadRouterApi.getCoverage();
          if (!dead) setRules(cov.categoryRules);
        } catch {
          /* category dropdown just stays empty — never fall back to a hardcoded list */
        }
      }
      try {
        const g = await leadRouterV2Api.listProductGroups();
        if (!dead) setGroups(g);
      } catch {
        /* override dropdown stays empty */
      }
      try {
        const s = await leadRouterV2Api.geoStates();
        if (!dead) { setStates(s); setGeoDown(false); }
      } catch {
        if (!dead) setGeoDown(true);
      }
    })();
    return () => { dead = true; };
  }, [open, categoryRules]);

  // State → districts (PincodeArea is district-grained; there is no city list).
  useEffect(() => {
    if (!form.state) { setDistricts([]); return; }
    let dead = false;
    void (async () => {
      try {
        const d = await leadRouterV2Api.geoDistricts(form.state);
        if (!dead) setDistricts(d);
      } catch {
        if (!dead) setDistricts([]);
      }
    })();
    return () => { dead = true; };
  }, [form.state]);

  const categories = useMemo(() => deriveCategoryOptions(rules), [rules]);
  const appTyped = useMemo(
    () => isCategoryApplicationTyped(rules, form.capturedCategory),
    [rules, form.capturedCategory],
  );
  const appTypes = useMemo(
    () => deriveApplicationTypeOptions(rules, form.capturedCategory),
    [rules, form.capturedCategory],
  );

  const set = <K extends keyof SimV2FormState>(k: K, v: SimV2FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  // ── Pincode lookup: 6 digits → derive + lock district/state; 3-5 → suggest ──
  const onPincodeInput = async (raw: string) => {
    const v = raw.replace(/\D/g, '').slice(0, 6);
    setForm((f) => ({ ...f, pincode: v, pincodeLocked: false }));
    setPinHits([]);
    if (isValidPincode(v)) {
      try {
        const geo = await leadRouterV2Api.geoPincode(v);
        setForm((f) => applyPincodeResolved(f, geo));
      } catch {
        setForm((f) => ({
          ...f,
          pincodeLocked: false,
        }));
      }
      return;
    }
    if (v.length >= 3) {
      try {
        setPinHits(await leadRouterV2Api.geoPincodes(v));
      } catch {
        setPinHits([]);
      }
    }
  };

  const run = async () => {
    setLoading(true);
    setError(null);
    setBatch(null);
    try {
      const payload = buildSimulateV2Payload(form);
      const r = await leadRouterV2Api.simulateV2(payload);
      setResult(r);
      // Bridge the v2 result onto the v1 shape the routing-graph highlighter
      // consumes (pre-existing window callback + the onResult prop).
      const legacy = toLegacyHighlight(payload, r);
      onResult?.(legacy.lead, legacy.result);
      (window as any).__lrSimCb?.(legacy.lead, legacy.result);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Simulation failed');
    } finally {
      setLoading(false);
    }
  };

  const batchLeads = useMemo(
    () => expandBatchLeads(form, varyBy, picked),
    [form, varyBy, picked],
  );
  const batchGuard = checkBatchSize(batchLeads);

  const runBatch = async () => {
    if (!batchGuard.ok) { setError(batchGuard.message); return; }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      setBatch(await leadRouterV2Api.simulateV2Batch(batchLeads));
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Batch simulation failed');
    } finally {
      setLoading(false);
    }
  };

  const batchValues =
    varyBy === 'state' ? states
    : varyBy === 'district' ? districts
    : pinHits.map((p) => p.pincode);

  const iStyle = SIM_CTRL;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(27,23,19,.34)',
          opacity: open ? 1 : 0,
          transition: 'opacity .24s',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 420,
          background: 'var(--ivory-0)',
          borderLeft: '1px solid var(--line)',
          boxShadow: 'var(--sh-4)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform .26s cubic-bezier(.2,.8,.3,1)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '13px 16px',
            borderBottom: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            flexShrink: 0,
          }}
        >
          <Play size={15} style={{ color: 'var(--forest-600)' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-0)' }}>
              Lead Simulator
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              v2 resolver over RoutingAssignment · read-only
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 26,
              height: 26,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--ink-3)',
              borderRadius: 5,
            }}
          >
            <X size={15} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {/* Inputs */}
          {/* Mode switch */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {(['single', 'batch'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => { setMode(m); setError(null); }}
                style={{
                  flex: 1,
                  height: 30,
                  fontSize: 12,
                  fontWeight: mode === m ? 600 : 400,
                  border: '1px solid var(--line)',
                  borderRadius: 7,
                  background: mode === m ? 'var(--ivory-2)' : 'var(--ivory-0)',
                  cursor: 'pointer',
                  color: mode === m ? 'var(--ink-0)' : 'var(--ink-2)',
                }}
              >
                {m === 'single' ? 'One lead' : 'Batch grid'}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
            {/* Product category — distinct capturedCategory off CategoryResolutionRule */}
            <SimField
              label="Product category"
              note={
                categories.length === 0
                  ? 'No category rules loaded — add CategoryResolutionRule rows first.'
                  : undefined
              }
            >
              <select
                value={form.capturedCategory}
                onChange={(e) => setForm((f) => applyCategoryChange(f, rules, e.target.value))}
                style={iStyle}
              >
                <option value="">(not set)</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </SimField>

            {/* Application type — only meaningful for app-typed categories */}
            <SimField
              label="Application type"
              note={
                form.capturedCategory && !appTyped ? APP_TYPE_DISABLED_NOTE : undefined
              }
            >
              <select
                value={form.applicationType}
                disabled={!appTyped}
                onChange={(e) => set('applicationType', e.target.value)}
                style={{ ...iStyle, opacity: appTyped ? 1 : 0.5, cursor: appTyped ? 'auto' : 'not-allowed' }}
              >
                <option value="">(any)</option>
                {appTypes.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </SimField>

            {/* Pincode — derives + locks district/state, mirrors geocode-at-import */}
            <SimField
              label="Pincode"
              note={
                form.pincodeLocked
                  ? 'District + state derived from PincodeArea and locked.'
                  : geoDown
                    ? 'Geo lookup unavailable — district/state stay manual.'
                    : 'Type 3+ digits for suggestions, 6 to resolve.'
              }
            >
              <input
                value={form.pincode}
                onChange={(e) => void onPincodeInput(e.target.value)}
                inputMode="numeric"
                placeholder="132103"
                style={iStyle}
              />
              {pinHits.length > 0 && !form.pincodeLocked && (
                <div style={{ marginTop: 5, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {pinHits.slice(0, 12).map((p) => (
                    <button
                      key={p.pincode}
                      type="button"
                      onClick={() => { setForm((f) => applyPincodeResolved(f, p)); setPinHits([]); }}
                      style={{
                        height: 24, padding: '0 8px', fontSize: 11,
                        border: '1px solid var(--line)', borderRadius: 6,
                        background: 'var(--ivory-0)', cursor: 'pointer', color: 'var(--ink-1)',
                      }}
                    >
                      {p.pincode} · {p.district}
                    </button>
                  ))}
                </div>
              )}
              {form.pincodeLocked && (
                <button
                  type="button"
                  onClick={() => setForm(applyPincodeCleared)}
                  style={{
                    marginTop: 5, height: 24, padding: '0 8px', fontSize: 11,
                    border: '1px solid var(--line)', borderRadius: 6,
                    background: 'var(--ivory-0)', cursor: 'pointer', color: 'var(--ink-2)',
                  }}
                >
                  Clear pincode
                </button>
              )}
            </SimField>

            <SimSwitch
              label="Pincode confident"
              value={form.pincodeConfident}
              onChange={(v) => set('pincodeConfident', v)}
              note={PINCODE_CONFIDENT_NOTE}
            />

            {/* State / District — from the global PincodeArea table */}
            <SimField label="State">
              <select
                value={form.state}
                disabled={form.pincodeLocked}
                onChange={(e) => setForm((f) => applyStateChange(f, e.target.value))}
                style={{ ...iStyle, opacity: form.pincodeLocked ? 0.6 : 1 }}
              >
                <option value="">(not set)</option>
                {(form.state && !states.includes(form.state) ? [form.state, ...states] : states).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </SimField>

            <SimField label="District">
              <select
                value={form.district}
                disabled={form.pincodeLocked || !form.state}
                onChange={(e) => set('district', e.target.value)}
                style={{ ...iStyle, opacity: form.pincodeLocked || !form.state ? 0.6 : 1 }}
              >
                <option value="">{form.state ? '(not set)' : 'Pick a state first'}</option>
                {(form.district && !districts.includes(form.district)
                  ? [form.district, ...districts]
                  : districts
                ).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </SimField>

            {/* City — free text ON PURPOSE: PincodeArea has no city column. */}
            <SimField label="City" note={CITY_NOTE}>
              <input
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
                placeholder="Panipat"
                style={iStyle}
                list="sim-v2-city-hints"
              />
              <datalist id="sim-v2-city-hints">
                {districts.map((d) => (
                  <option key={d} value={d} />
                ))}
              </datalist>
            </SimField>

            {/* Interest level — the closed enum decideRouteV2 normalises to. */}
            <SimField label="Interest level">
              <select
                value={form.interestLevel}
                onChange={(e) => set('interestLevel', e.target.value as SimV2FormState['interestLevel'])}
                style={iStyle}
              >
                <option value="">(not set)</option>
                <option value="HOT">HOT</option>
                <option value="WARM">WARM</option>
                <option value="COLD">COLD</option>
              </select>
            </SimField>

            <div>
              <label style={SIM_LABEL}>B2B priority</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { label: 'Not set', v: null },
                  { label: 'Yes', v: true },
                  { label: 'No', v: false },
                ].map(({ label: l, v }) => (
                  <button
                    key={String(v)}
                    type="button"
                    onClick={() => set('b2b', v as boolean | null)}
                    style={{
                      flex: 1,
                      height: 30,
                      fontSize: 12,
                      fontWeight: form.b2b === v ? 600 : 400,
                      border: '1px solid var(--line)',
                      borderRadius: 7,
                      background: form.b2b === v ? 'var(--ivory-2)' : 'var(--ivory-0)',
                      cursor: 'pointer',
                      color: form.b2b === v ? 'var(--ink-0)' : 'var(--ink-2)',
                    }}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <SimNote>“Not set” is sent as isB2B = false.</SimNote>
            </div>

            <SimSwitch
              label="Human touched"
              value={form.humanTouched}
              onChange={(v) => set('humanTouched', v)}
              note={HUMAN_TOUCHED_NOTE}
            />

            {/* Advanced — test a territory grid without going through categories */}
            <SimField
              label="Product group override (advanced)"
              note={
                form.productGroupOverride
                  ? 'Category + application type are ignored while an override is set.'
                  : undefined
              }
            >
              <select
                value={form.productGroupOverride}
                onChange={(e) => set('productGroupOverride', e.target.value)}
                style={iStyle}
              >
                <option value="">(off — resolve from category)</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.key}>{g.label || g.key}</option>
                ))}
              </select>
            </SimField>

            {/* Expected owner → turns a run into a pass/fail assertion */}
            <div
              style={{
                borderTop: '1px solid var(--line)',
                paddingTop: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <div style={{ ...SIM_LABEL, marginBottom: 0 }}>Expected outcome (optional)</div>
              <SimNote>
                Set any of these and the run becomes a pass/fail check instead of just a trace.
              </SimNote>
              <SimField label="Expected owner">
                <select
                  value={form.expectedOwnerUserId}
                  onChange={(e) => set('expectedOwnerUserId', e.target.value)}
                  style={iStyle}
                >
                  <option value="">(no expectation)</option>
                  {staff.map((s) => (
                    <option key={s.id} value={s.id}>{staffName(s)}</option>
                  ))}
                </select>
              </SimField>
              <SimField label="Expected dealer">
                <select
                  value={form.expectedDealerId}
                  onChange={(e) => set('expectedDealerId', e.target.value)}
                  style={iStyle}
                >
                  <option value="">(no expectation)</option>
                  {dealers.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </SimField>
              <SimField label="Expected destination">
                <select
                  value={form.expectedDestination}
                  onChange={(e) =>
                    set('expectedDestination', e.target.value as SimV2FormState['expectedDestination'])
                  }
                  style={iStyle}
                >
                  <option value="">(no expectation)</option>
                  <option value="user">Assigned to person</option>
                  <option value="dealer_dispatch">Dispatched to dealer</option>
                  <option value="triage">Triage</option>
                </select>
              </SimField>
            </div>

            {/* Batch axis */}
            {mode === 'batch' && (
              <div
                style={{
                  borderTop: '1px solid var(--line)',
                  paddingTop: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <div style={{ ...SIM_LABEL, marginBottom: 0 }}>Batch grid</div>
                <SimNote>
                  Runs the same lead across every value you tick. One deps hydration for the
                  whole grid — max {BATCH_LIMIT}.
                </SimNote>
                <SimField label="Vary by">
                  <select
                    value={varyBy}
                    onChange={(e) => { setVaryBy(e.target.value as BatchVaryBy); setPicked([]); }}
                    style={iStyle}
                  >
                    <option value="state">State</option>
                    <option value="district">District (within the state above)</option>
                    <option value="pincode">Pincode (from the suggestions above)</option>
                  </select>
                </SimField>
                <div>
                  <label style={SIM_LABEL}>
                    Values · {picked.length} selected
                  </label>
                  {batchValues.length === 0 ? (
                    <SimNote>
                      {varyBy === 'district'
                        ? 'Pick a state first.'
                        : varyBy === 'pincode'
                          ? 'Type 3+ pincode digits above to load candidates.'
                          : 'No states loaded.'}
                    </SimNote>
                  ) : (
                    <>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                        <button
                          type="button"
                          onClick={() => setPicked(batchValues.slice(0, BATCH_LIMIT))}
                          style={{
                            height: 24, padding: '0 8px', fontSize: 11,
                            border: '1px solid var(--line)', borderRadius: 6,
                            background: 'var(--ivory-0)', cursor: 'pointer', color: 'var(--ink-2)',
                          }}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          onClick={() => setPicked([])}
                          style={{
                            height: 24, padding: '0 8px', fontSize: 11,
                            border: '1px solid var(--line)', borderRadius: 6,
                            background: 'var(--ivory-0)', cursor: 'pointer', color: 'var(--ink-2)',
                          }}
                        >
                          Clear
                        </button>
                      </div>
                      <div
                        style={{
                          maxHeight: 168,
                          overflowY: 'auto',
                          border: '1px solid var(--line)',
                          borderRadius: 7,
                          padding: 7,
                        }}
                      >
                        {batchValues.map((v) => (
                          <label
                            key={v}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 7,
                              fontSize: 12, color: 'var(--ink-1)', padding: '2px 0', cursor: 'pointer',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={picked.includes(v)}
                              onChange={(e) =>
                                setPicked((p) => (e.target.checked ? [...p, v] : p.filter((x) => x !== v)))
                              }
                            />
                            {v}
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                  {batchGuard.message && <SimNote>{batchGuard.message}</SimNote>}
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={mode === 'batch' ? runBatch : run}
            disabled={loading || (mode === 'batch' && !batchGuard.ok)}
            style={{
              width: '100%',
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
              fontSize: 13,
              fontWeight: 600,
              border: 'none',
              borderRadius: 9,
              background: 'var(--forest)',
              color: '#fff',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              marginBottom: 14,
            }}
          >
            <Play size={13} />
            {loading
              ? 'Simulating…'
              : mode === 'batch'
                ? `Run ${batchGuard.count} lead${batchGuard.count === 1 ? '' : 's'}`
                : 'Run simulation'}
          </button>

          {error && (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: 8,
                background: 'var(--danger-bg)',
                border: '1px solid #EFCFCA',
                color: 'var(--danger)',
                fontSize: 12,
                marginBottom: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 7,
              }}
            >
              <AlertCircle size={14} /> {error}
            </div>
          )}

          {batch && (
            <>
              <div style={{ ...SIM_LABEL, marginBottom: 9 }}>
                Batch result · {batch.summary.total} leads
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                {[
                  { l: 'Person', n: batch.summary.byDestination.user },
                  { l: 'Dealer', n: batch.summary.byDestination.dealer_dispatch },
                  { l: 'Triage', n: batch.summary.byDestination.triage },
                ].map(({ l, n }) => (
                  <span
                    key={l}
                    style={{
                      fontSize: 11, padding: '3px 8px', borderRadius: 6,
                      border: '1px solid var(--line)', background: 'var(--ivory-1)',
                      color: 'var(--ink-1)',
                    }}
                  >
                    {l} <b>{n}</b>
                  </span>
                ))}
                {batch.summary.asserted > 0 && (
                  <span
                    style={{
                      fontSize: 11, padding: '3px 8px', borderRadius: 6,
                      border: `1px solid ${batch.summary.failed ? '#EFCFCA' : 'var(--forest-100)'}`,
                      background: batch.summary.failed ? 'var(--danger-bg)' : 'var(--forest-50)',
                      color: batch.summary.failed ? 'var(--danger)' : 'var(--forest-600)',
                    }}
                  >
                    {batch.summary.passed}/{batch.summary.asserted} passed
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
                {batch.results.map((row) => (
                  <div
                    key={row.index}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '6px 9px', borderRadius: 7,
                      border: '1px solid var(--line)', background: 'var(--ivory-0)',
                      fontSize: 11.5,
                    }}
                  >
                    <span style={{ flex: 1, color: 'var(--ink-0)', fontWeight: 500 }}>
                      {row.label ?? `#${row.index}`}
                    </span>
                    <span style={{ color: 'var(--ink-2)' }}>
                      {row.matchType ? `${row.matchType}:${row.matchValue ?? ''}` : '—'}
                    </span>
                    <span style={{ color: 'var(--ink-1)' }}>
                      {row.ownerName ?? row.dealerName ?? row.triageReason ?? row.destination}
                    </span>
                    {row.assertion && (
                      row.assertion.pass
                        ? <Check size={12} style={{ color: 'var(--forest-600)' }} />
                        : <X size={12} style={{ color: 'var(--danger)' }} />
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {result && (
            <>
              {/* Assertion banner — only when the run carried an expectation */}
              {(() => {
                const a = describeAssertion(result);
                if (!a.present) return null;
                return (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: '10px 12px',
                      borderRadius: 9,
                      background: a.pass ? 'var(--forest-50)' : 'var(--danger-bg)',
                      border: `1px solid ${a.pass ? 'var(--forest-100)' : '#EFCFCA'}`,
                      color: a.pass ? 'var(--forest-600)' : 'var(--danger)',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      {a.pass ? <Check size={14} /> : <AlertCircle size={14} />} {a.label}
                    </div>
                    {a.mismatches.map((m) => (
                      <div key={m} style={{ fontWeight: 400, fontSize: 11, marginTop: 4 }}>{m}</div>
                    ))}
                  </div>
                );
              })()}

              {/* Notes the backend surfaced (pincode misses, override active, …) */}
              {result.notes.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  {result.notes.map((n) => (
                    <div
                      key={n}
                      style={{
                        fontSize: 11, color: 'var(--ink-2)', padding: '6px 9px',
                        borderRadius: 7, background: 'var(--ivory-1)',
                        border: '1px solid var(--line)', marginBottom: 4,
                      }}
                    >
                      {n}
                    </div>
                  ))}
                </div>
              )}

              {/* Resolved lead echo — shows what the pincode filled in */}
              <div style={{ ...SIM_LABEL, marginBottom: 7 }}>Resolved lead</div>
              <div
                style={{
                  display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14,
                }}
              >
                {[
                  ['group', result.trace.group],
                  ['tier', result.trace.tier],
                  ['city', result.resolvedLead.city],
                  ['district', result.resolvedLead.district],
                  ['state', result.resolvedLead.state],
                  ['pincode', result.resolvedLead.pincode],
                  ['pin-confident', String(result.resolvedLead.pincodeConfident)],
                  ['B2B', String(result.resolvedLead.isB2B)],
                  ['human-touched', String(result.resolvedLead.humanTouched)],
                ]
                  .filter(([, v]) => v !== null && v !== undefined && v !== '')
                  .map(([k, v]) => (
                    <span
                      key={k as string}
                      style={{
                        fontSize: 10.5, padding: '3px 7px', borderRadius: 6,
                        background: 'var(--ivory-1)', border: '1px solid var(--line)',
                        color: 'var(--ink-2)',
                      }}
                    >
                      {k}: <b style={{ color: 'var(--ink-0)' }}>{v}</b>
                    </span>
                  ))}
              </div>

              {/* Candidates — winner + every rejected one */}
              {result.trace.candidates.length > 0 && (
                <>
                  <div style={{ ...SIM_LABEL, marginBottom: 7 }}>
                    Candidates · layers {result.trace.layerRun.join(' → ') || '—'}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
                    {result.trace.candidates.map((c, i) => (
                      <div
                        key={`${c.layer}-${c.userId ?? c.dealerId ?? i}-${c.matchType}-${c.matchValue}`}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '6px 9px', borderRadius: 7, fontSize: 11.5,
                          border: `1px solid ${c.chosen ? 'var(--forest-100)' : 'var(--line)'}`,
                          background: c.chosen ? 'var(--forest-50)' : 'var(--ivory-0)',
                        }}
                      >
                        <span
                          style={{
                            fontSize: 9.5, fontWeight: 700, letterSpacing: 0.05,
                            color: 'var(--ink-3)', width: 52,
                          }}
                        >
                          {c.layer}
                        </span>
                        <span style={{ flex: 1, color: 'var(--ink-0)', fontWeight: c.chosen ? 600 : 400 }}>
                          {c.name ?? c.userId ?? c.dealerId ?? '—'}
                          {c.primary && <span style={{ color: 'var(--ink-3)' }}> · primary</span>}
                        </span>
                        <span style={{ color: 'var(--ink-2)' }}>
                          {c.matchType}:{c.matchValue} · r{c.rank}
                        </span>
                        {c.chosen
                          ? <Check size={12} style={{ color: 'var(--forest-600)' }} />
                          : <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>{c.rejectReason ?? 'not chosen'}</span>}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Trace */}
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--ink-2)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.06,
                  marginBottom: 9,
                }}
              >
                Decision trace
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {result.trace.lines.map((line, i) => {
                  const isLast = i === result.trace.lines.length - 1;
                  return (
                    <div
                      key={i}
                      style={{ display: 'flex', gap: 10, paddingBottom: isLast ? 0 : 10, position: 'relative' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <span
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 10,
                            background: isLast ? 'var(--forest)' : 'var(--ivory-2)',
                            border: `1.5px solid ${isLast ? 'var(--forest)' : 'var(--line)'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 10,
                            fontWeight: 700,
                            color: isLast ? '#fff' : 'var(--ink-3)',
                          }}
                        >
                          {isLast ? <Check size={11} /> : i + 1}
                        </span>
                        {!isLast && (
                          <span
                            style={{
                              width: 1.5,
                              flex: 1,
                              background: 'var(--line)',
                              minHeight: 12,
                            }}
                          />
                        )}
                      </div>
                      <div style={{ flex: 1, paddingTop: 2, paddingBottom: isLast ? 0 : 2 }}>
                        <div
                          style={{
                            fontSize: 11.5,
                            color: isLast ? 'var(--forest-600)' : 'var(--ink-1)',
                            fontWeight: isLast ? 600 : 400,
                            lineHeight: 1.45,
                          }}
                        >
                          {line}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Result box */}
              <div
                style={{
                  marginTop: 12,
                  padding: 14,
                  borderRadius: 11,
                  background:
                    result.destination === 'triage' ? 'var(--saffron-50)' : 'var(--forest-50)',
                  border: `1px solid ${result.destination === 'triage' ? 'var(--saffron-100)' : 'var(--forest-100)'}`,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.08,
                    color:
                      result.destination === 'triage'
                        ? 'var(--saffron-600)'
                        : 'var(--forest-600)',
                    marginBottom: 6,
                  }}
                >
                  {destinationLabel(result.destination)}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  {result.destination !== 'triage' && (
                    <AvatarChip name={winnerLabel(result)} size={28} />
                  )}
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-0)' }}>
                    {winnerLabel(result)}
                  </div>
                </div>
                {result.trace.group && (
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>
                    Routing group: <b style={{ color: 'var(--ink-1)' }}>{result.trace.group}</b>
                  </div>
                )}
                {(result.handoffToManager || result.notifyOnly) && (
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>
                    {result.handoffToManager && <b style={{ color: 'var(--ink-1)' }}>escalated to manager</b>}
                    {result.handoffToManager && result.notifyOnly && ' · '}
                    {result.notifyOnly && <b style={{ color: 'var(--ink-1)' }}>notify-only (owner unchanged)</b>}
                  </div>
                )}
                <div style={{ fontSize: 10.5, color: 'var(--ink-3)', marginTop: 8 }}>
                  Read-only — nothing was assigned.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Structural rules tables (category / territory / dealers) ─────────────────

export function StructuralRulesPanel({ coverage, onRefresh }: { coverage: CoverageData; onRefresh: () => void }) {
  const [tab, setTab] = useState<'category' | 'territory' | 'dealers'>('category');
  const [saving, setSaving] = useState(false);

  const patchCat = async (id: string, field: string, value: string | number) => {
    setSaving(true);
    try {
      await leadRouterApi.patchCategoryRule(id, { [field]: value });
      onRefresh();
    } catch {
      /* silent — user can retry */
    } finally {
      setSaving(false);
    }
  };

  const deleteCat = async (id: string) => {
    if (!confirm('Delete this category rule?')) return;
    setSaving(true);
    try {
      await leadRouterApi.deleteCategoryRule(id);
      onRefresh();
    } catch {
      /* noop */
    } finally {
      setSaving(false);
    }
  };

  const patchTerr = async (id: string, field: string, value: string | number | null) => {
    setSaving(true);
    try {
      await leadRouterApi.patchTerritoryRule(id, { [field]: value } as any);
      onRefresh();
    } catch {
      /* noop */
    } finally {
      setSaving(false);
    }
  };

  const deleteTerr = async (id: string) => {
    if (!confirm('Delete this territory rule?')) return;
    setSaving(true);
    try {
      await leadRouterApi.deleteTerritoryRule(id);
      onRefresh();
    } catch {
      /* noop */
    } finally {
      setSaving(false);
    }
  };

  const deactivateDealer = async (id: string) => {
    if (!confirm('Deactivate this dealer?')) return;
    setSaving(true);
    try {
      await leadRouterApi.deactivateDealer(id);
      onRefresh();
    } catch {
      /* noop */
    } finally {
      setSaving(false);
    }
  };

  const thStyle: React.CSSProperties = {
    padding: '7px 10px',
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--ink-3)',
    textTransform: 'uppercase',
    letterSpacing: 0.06,
    textAlign: 'left',
    borderBottom: '1px solid var(--line)',
    background: 'var(--ivory-1)',
  };
  const tdStyle: React.CSSProperties = {
    padding: '8px 10px',
    fontSize: 12.5,
    color: 'var(--ink-1)',
    borderBottom: '1px solid var(--line)',
    verticalAlign: 'middle',
  };

  const staffById = Object.fromEntries(coverage.staff.map((s) => [s.id, s]));

  return (
    <div>
      {/* Tab bar */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid var(--line)',
          marginBottom: 14,
        }}
      >
        {(
          [
            { id: 'category', label: `Category rules (${coverage.categoryRules.length})` },
            { id: 'territory', label: `Territory rules (${coverage.territoryRules.length})` },
            { id: 'dealers', label: `Dealers (${coverage.dealers.length})` },
          ] as const
        ).map(({ id, label }) => {
          const on = tab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              style={{
                padding: '8px 14px',
                fontSize: 12.5,
                fontWeight: on ? 600 : 400,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: on ? 'var(--ink-0)' : 'var(--ink-3)',
                borderBottom: on ? '2px solid var(--saffron)' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {tab === 'category' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Application type</th>
                <th style={thStyle}>Routing group</th>
                <th style={thStyle}>Rank</th>
                <th style={thStyle} />
              </tr>
            </thead>
            <tbody>
              {coverage.categoryRules.map((r) => (
                <tr key={r.id}>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: '2px 7px',
                        background: 'var(--ivory-2)',
                        border: '1px solid var(--line)',
                        borderRadius: 5,
                        fontFamily: 'var(--ff-mono)',
                        fontSize: 11,
                      }}
                    >
                      {r.capturedCategory}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--ink-3)' }}>
                    {r.applicationType ?? '—'}
                  </td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: '2px 7px',
                        background: 'var(--forest-50)',
                        color: 'var(--forest-600)',
                        border: '1px solid var(--forest-100)',
                        borderRadius: 5,
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      {r.routingGroup}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'var(--ff-mono)' }}>{r.rank}</td>
                  <td style={{ ...tdStyle, width: 40 }}>
                    <button
                      type="button"
                      onClick={() => deleteCat(r.id)}
                      disabled={saving}
                      style={{
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 'none',
                        background: 'transparent',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        color: 'var(--danger)',
                        opacity: 0.7,
                        borderRadius: 4,
                      }}
                    >
                      <X size={13} />
                    </button>
                  </td>
                </tr>
              ))}
              {coverage.categoryRules.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: 'var(--ink-3)', padding: '20px 0' }}>
                    No category rules found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'territory' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr>
                <th style={thStyle}>Product group</th>
                <th style={thStyle}>Match type</th>
                <th style={thStyle}>Match value</th>
                <th style={thStyle}>Owner</th>
                <th style={thStyle}>Rank</th>
                <th style={thStyle} />
              </tr>
            </thead>
            <tbody>
              {coverage.territoryRules.map((r) => {
                const owner = r.ownerUserId ? staffById[r.ownerUserId] : null;
                return (
                  <tr key={r.id}>
                    <td style={tdStyle}>{r.productGroup}</td>
                    <td style={tdStyle}>
                      <StatusPill tone={r.matchType === 'catchall' ? 'amber' : 'neutral'}>
                        {r.matchType}
                      </StatusPill>
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'var(--ff-mono)', fontSize: 11 }}>
                      {r.matchValue}
                    </td>
                    <td style={tdStyle}>
                      {owner ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <AvatarChip name={staffName(owner)} size={20} />
                          <span>{staffName(owner)}</span>
                        </div>
                      ) : r.dealerId ? (
                        <span style={{ color: 'var(--ink-3)' }}>Dealer</span>
                      ) : (
                        <span style={{ color: 'var(--ink-3)' }}>—</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'var(--ff-mono)' }}>{r.rank}</td>
                    <td style={{ ...tdStyle, width: 40 }}>
                      <button
                        type="button"
                        onClick={() => deleteTerr(r.id)}
                        disabled={saving}
                        style={{
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: 'none',
                          background: 'transparent',
                          cursor: saving ? 'not-allowed' : 'pointer',
                          color: 'var(--danger)',
                          opacity: 0.7,
                          borderRadius: 4,
                        }}
                      >
                        <X size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {coverage.territoryRules.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: 'var(--ink-3)', padding: '20px 0' }}>
                    No territory rules found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'dealers' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr>
                <th style={thStyle}>Dealer name</th>
                <th style={thStyle}>WA group ID</th>
                <th style={thStyle}>Follow-up owner</th>
                <th style={thStyle} />
              </tr>
            </thead>
            <tbody>
              {coverage.dealers.map((d) => {
                const owner = d.followUpOwnerId ? staffById[d.followUpOwnerId] : null;
                return (
                  <tr key={d.id}>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <Users size={14} style={{ color: 'var(--ink-3)' }} />
                        {d.name}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'var(--ff-mono)', fontSize: 11, color: 'var(--ink-3)' }}>
                      {d.waGroupId ?? '—'}
                    </td>
                    <td style={tdStyle}>
                      {owner ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <AvatarChip name={staffName(owner)} size={18} />
                          <span>{staffName(owner)}</span>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--ink-3)' }}>—</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, width: 40 }}>
                      <button
                        type="button"
                        onClick={() => deactivateDealer(d.id)}
                        disabled={saving}
                        title="Deactivate dealer"
                        style={{
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: 'none',
                          background: 'transparent',
                          cursor: saving ? 'not-allowed' : 'pointer',
                          color: 'var(--danger)',
                          opacity: 0.7,
                          borderRadius: 4,
                        }}
                      >
                        <X size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {coverage.dealers.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: 'var(--ink-3)', padding: '20px 0' }}>
                    No dealers configured.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function LeadRouterPage() {
  // ── Data state ──────────────────────────────────────────────────────────────
  const [coverage, setCoverage] = useState<CoverageData | null>(null);
  const [overlayRules, setOverlayRules] = useState<OverlayRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [simOpen, setSimOpen] = useState(false);
  const [simLead, setSimLead] = useState<SimulateInput | null>(null);
  const [simResult, setSimResult] = useState<SimulateResult | null>(null);
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [savingRuleIds, setSavingRuleIds] = useState<Set<string>>(new Set());
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // ── Drag ref for priority reorder ────────────────────────────────────────────
  const dragRef = useRef<{ idx: number; y: number } | null>(null);

  // Register simulator callback for graph highlighting
  useEffect(() => {
    (window as any).__lrSimCb = (lead: SimulateInput, result: SimulateResult) => {
      setSimLead(lead);
      setSimResult(result);
    };
    return () => { delete (window as any).__lrSimCb; };
  }, []);

  // ── Load ─────────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cov, overlay] = await Promise.all([
        leadRouterApi.getCoverage(),
        leadRouterApi.listOverlayRules(),
      ]);
      setCoverage(cov);
      setOverlayRules(overlay);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 1800);
  };

  // ── Settings toggle ───────────────────────────────────────────────────────────
  const toggleAutoRouter = async (v: boolean) => {
    if (!coverage) return;
    setCoverage((c) => c && { ...c, settings: { ...c.settings, autoRouterEnabled: v } });
    setSettingsSaving(true);
    try {
      await leadRouterApi.updateSettings({ autoRouterEnabled: v });
      showToast('Auto-router ' + (v ? 'enabled' : 'disabled'));
    } catch {
      setCoverage((c) => c && { ...c, settings: { ...c.settings, autoRouterEnabled: !v } });
    } finally {
      setSettingsSaving(false);
    }
  };

  const toggleDealerWA = async (v: boolean) => {
    if (!coverage) return;
    setCoverage((c) => c && { ...c, settings: { ...c.settings, dealerWhatsAppSendEnabled: v } });
    setSettingsSaving(true);
    try {
      await leadRouterApi.updateSettings({ dealerWhatsAppSendEnabled: v });
      showToast('Dealer WhatsApp ' + (v ? 'enabled' : 'disabled'));
    } catch {
      setCoverage((c) => c && { ...c, settings: { ...c.settings, dealerWhatsAppSendEnabled: !v } });
    } finally {
      setSettingsSaving(false);
    }
  };

  // ── Overlay rule mutations ────────────────────────────────────────────────────
  const addOverlayRule = async () => {
    const newPriority = overlayRules.length > 0
      ? Math.max(...overlayRules.map((r) => r.priority)) + 1
      : 0;
    try {
      const created = await leadRouterApi.createOverlayRule({
        priority: newPriority,
        name: 'New overlay rule',
        conditions: { op: 'AND', items: [{ field: 'capturedCategory', op: 'is', value: '' }] },
        target: { action: 'manual_deepika' },
        enabled: true,
      });
      setOverlayRules((rs) => [...rs, created]);
      setExpandedRuleId(created.id);
      showToast('Rule created');
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Failed to create rule');
    }
  };

  const saveOverlayRule = async (rule: OverlayRule) => {
    setSavingRuleIds((s) => new Set([...s, rule.id]));
    try {
      const updated = await leadRouterApi.patchOverlayRule(rule.id, {
        name: rule.name,
        conditions: rule.conditions,
        target: rule.target,
        enabled: rule.enabled,
      });
      setOverlayRules((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
      showToast('Saved');
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Failed to save rule');
    } finally {
      setSavingRuleIds((s) => { const n = new Set(s); n.delete(rule.id); return n; });
    }
  };

  // Debounced auto-save on rule change
  const ruleTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const onRuleChange = (rule: OverlayRule) => {
    setOverlayRules((rs) => rs.map((r) => (r.id === rule.id ? rule : r)));
    clearTimeout(ruleTimers.current[rule.id]);
    ruleTimers.current[rule.id] = setTimeout(() => saveOverlayRule(rule), 800);
  };

  const deleteOverlayRule = async (id: string) => {
    if (!confirm('Delete this overlay rule?')) return;
    try {
      await leadRouterApi.deleteOverlayRule(id);
      setOverlayRules((rs) => rs.filter((r) => r.id !== id));
      showToast('Rule deleted');
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Delete failed');
    }
  };

  // ── Drag to reorder overlay rules ─────────────────────────────────────────────
  const sorted = useMemo(
    () => [...overlayRules].sort((a, b) => a.priority - b.priority),
    [overlayRules],
  );

  const onPriorityDrag = (idx: number) => (e: React.PointerEvent) => {
    e.preventDefault();
    dragRef.current = { idx, y: e.clientY };

    const move = async (ev: PointerEvent) => {
      if (!dragRef.current) return;
      const dy = ev.clientY - dragRef.current.y;
      if (Math.abs(dy) > 54) {
        const dir = dy > 0 ? 1 : -1;
        const from = dragRef.current.idx;
        const to = from + dir;
        if (to >= 0 && to < sorted.length) {
          const reordered = [...sorted];
          const tmp = reordered[from];
          reordered[from] = reordered[to];
          reordered[to] = tmp;
          const withPriority = reordered.map((r, i) => ({ ...r, priority: i }));
          setOverlayRules(withPriority);
          dragRef.current.idx = to;
          dragRef.current.y = ev.clientY;
          // persist reorder
          try {
            await leadRouterApi.reorderOverlayRules(
              withPriority.map((r) => ({ id: r.id, priority: r.priority })),
            );
          } catch {
            /* silent */
          }
          showToast('Priority updated');
        }
      }
    };

    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      dragRef.current = null;
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: 'center',
          color: 'var(--ink-3)',
          fontSize: 14,
        }}
      >
        Loading Lead Auto-Router…
      </div>
    );
  }

  if (error || !coverage) {
    return (
      <div style={{ padding: 40 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            color: 'var(--danger)',
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          <AlertCircle size={16} />
          {error ?? 'No data'}
        </div>
        <button
          type="button"
          onClick={refresh}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 14px',
            fontSize: 13,
            border: '1px solid var(--line)',
            borderRadius: 8,
            background: 'var(--ivory-0)',
            cursor: 'pointer',
            color: 'var(--ink-1)',
          }}
        >
          <RefreshCcw size={13} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--ivory-0)',
        position: 'relative',
      }}
    >
      {/* ── Page header ────────────────────────────────────────────────────────── */}
      <div
        style={{
          padding: '12px 24px',
          borderBottom: '1px solid var(--line)',
          background: 'var(--ivory-0)',
          flexShrink: 0,
        }}
      >
        {/* Breadcrumb */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 12,
            color: 'var(--ink-3)',
            marginBottom: 6,
          }}
        >
          <span>Admin</span>
          <ChevronRight size={11} style={{ opacity: 0.5 }} />
          <span>Builders</span>
          <ChevronRight size={11} style={{ opacity: 0.5 }} />
          <span style={{ color: 'var(--ink-1)' }}>Lead Auto-Router</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <h1
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: 'var(--ink-0)',
                margin: 0,
                letterSpacing: -0.3,
              }}
            >
              Lead Auto-Router
            </h1>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 3 }}>
              Visualise &amp; edit how incoming leads are auto-assigned
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Live hint */}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 11,
                color: 'var(--warn)',
                background: 'var(--warn-bg)',
                border: '1px solid #EBD9B0',
                padding: '4px 9px',
                borderRadius: 999,
                fontWeight: 500,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: 'var(--warn)',
                  boxShadow: '0 0 0 3px rgba(198,124,27,.18)',
                }}
              />
              Live — changes apply immediately
            </span>

            <button
              type="button"
              onClick={refresh}
              title="Reload from server"
              style={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--line)',
                borderRadius: 8,
                background: 'var(--ivory-0)',
                cursor: 'pointer',
                color: 'var(--ink-2)',
              }}
            >
              <RefreshCcw size={14} />
            </button>

            <button
              type="button"
              onClick={() => setSimOpen(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 34,
                padding: '0 14px',
                fontSize: 13,
                fontWeight: 600,
                border: 'none',
                borderRadius: 9,
                background: 'var(--forest)',
                color: '#fff',
                cursor: 'pointer',
              }}
            >
              <Play size={13} /> Lead simulator
            </button>
          </div>
        </div>
      </div>

      {/* ── Global controls bar ────────────────────────────────────────────────── */}
      <div
        style={{
          padding: '9px 24px',
          borderBottom: '1px solid var(--line)',
          background: 'var(--ivory-1)',
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12.5,
            fontWeight: 500,
            color: 'var(--ink-1)',
            cursor: 'pointer',
            opacity: settingsSaving ? 0.7 : 1,
          }}
        >
          <Toggle on={coverage.settings.autoRouterEnabled} onChange={toggleAutoRouter} />
          Auto-router {coverage.settings.autoRouterEnabled ? 'ON' : 'OFF'}
        </label>

        <div style={{ width: 1, height: 20, background: 'var(--line)' }} />

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12.5,
            fontWeight: 500,
            color: 'var(--ink-1)',
            cursor: 'pointer',
            opacity: settingsSaving ? 0.7 : 1,
          }}
        >
          <Toggle
            on={coverage.settings.dealerWhatsAppSendEnabled ?? false}
            onChange={toggleDealerWA}
            tone="saffron"
          />
          Dealer WhatsApp
        </label>

        <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          Fallback action: <b style={{ color: 'var(--ink-1)' }}>{coverage.settings.fallbackAction}</b>
        </span>
      </div>

      {/* ── Main body: graph left | rules right ───────────────────────────────── */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* Graph column */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            borderRight: '1px solid var(--line)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              padding: '10px 22px 0',
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              flexShrink: 0,
            }}
          >
            Routing map
          </div>

          <RouterGraph
            overlayRules={sorted}
            staff={coverage.staff}
            dealers={coverage.dealers}
            activeLead={simLead}
            simResult={simResult}
          />

          {/* Structural rules section */}
          <div style={{ padding: '0 22px 22px' }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--ink-3)',
                textTransform: 'uppercase',
                letterSpacing: 0.08,
                marginBottom: 12,
              }}
            >
              Structural routing tables
            </div>
            <StructuralRulesPanel coverage={coverage} onRefresh={refresh} />
          </div>
        </div>

        {/* Overlay rules list */}
        <div
          style={{
            width: 540,
            flexShrink: 0,
            overflowY: 'auto',
            background: 'var(--ivory-1)',
            padding: 16,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--ink-3)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.08,
                }}
              >
                Overlay rules · drag to set priority
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>
                Evaluated BEFORE the 4-stage router. First match wins.
              </div>
            </div>
            <button
              type="button"
              onClick={addOverlayRule}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                height: 30,
                padding: '0 11px',
                fontSize: 12,
                fontWeight: 500,
                border: '1px solid var(--line)',
                borderRadius: 7,
                background: 'var(--ivory-0)',
                cursor: 'pointer',
                color: 'var(--ink-1)',
              }}
            >
              <Plus size={12} /> Add rule
            </button>
          </div>

          {/* Rule cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {sorted.length === 0 && (
              <div
                style={{
                  padding: '24px 16px',
                  textAlign: 'center',
                  color: 'var(--ink-3)',
                  fontSize: 13,
                  border: '1px dashed var(--line)',
                  borderRadius: 10,
                }}
              >
                No overlay rules yet. Click "Add rule" to create one.
              </div>
            )}
            {sorted.map((rule, i) => {
              const simStatus =
                simResult && simLead
                  ? simResult.overlayRuleId === rule.id
                    ? 'matched'
                    : !rule.enabled
                    ? 'skipped'
                    : null
                  : null;

              return (
                <OverlayRuleCard
                  key={rule.id}
                  rule={rule}
                  expanded={expandedRuleId === rule.id}
                  onToggleExpand={() =>
                    setExpandedRuleId((id) => (id === rule.id ? null : rule.id))
                  }
                  onChange={onRuleChange}
                  onDragStart={onPriorityDrag(i)}
                  onDelete={() => deleteOverlayRule(rule.id)}
                  simStatus={simStatus as 'matched' | 'skipped' | null}
                  staff={coverage.staff}
                  dealers={coverage.dealers}
                  saving={savingRuleIds.has(rule.id)}
                />
              );
            })}
          </div>

          {/* Fallback box */}
          <div
            style={{
              marginTop: 14,
              padding: '11px 12px',
              borderRadius: 11,
              background: 'var(--saffron-50)',
              border: '1px dashed var(--saffron-100)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  background: 'var(--saffron-100)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Zap size={13} style={{ color: 'var(--saffron-600)' }} />
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink-0)' }}>
                  Fallback · unmatched leads
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  Action: <b>{coverage.settings.fallbackAction}</b> (configured in
                  TenantConfig)
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Simulator drawer ───────────────────────────────────────────────────── */}
      <SimulatorDrawer
        open={simOpen}
        onClose={() => setSimOpen(false)}
        staff={coverage.staff}
        dealers={coverage.dealers}
        categoryRules={coverage.categoryRules}
        onResult={(lead, result) => {
          setSimLead(lead);
          setSimResult(result);
        }}
      />

      {/* ── Toast ──────────────────────────────────────────────────────────────── */}
      {toast && (
        <div
          style={{
            position: 'absolute',
            bottom: 18,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 80,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '9px 16px',
            borderRadius: 10,
            background: 'var(--ink-0)',
            color: 'var(--ivory-0)',
            boxShadow: 'var(--sh-3)',
            fontSize: 13,
            fontWeight: 500,
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: 8,
              background: 'var(--forest)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Check size={10} style={{ color: '#fff' }} />
          </span>
          {toast}
        </div>
      )}
    </div>
  );
}
